import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as companyApi from '../../services/company-api.js';
import type { Company } from '../../services/company-api.js';

type DashboardTab = 'specs' | 'backlog' | 'docs';

@customElement('cp-project-dashboard')
export class CpProjectDashboard extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Number }) projectId = 0;
  @property({ type: String }) projectName = '';
  @property({ type: String }) projectPath = '';
  @property({ type: Number }) companyId: number | null = null;

  @state() private _activeTab: DashboardTab = 'specs';
  @state() private _companies: Company[] = [];
  @state() private _assigningCompany = false;
  @state() private _currentCompanyId: number | null = null;

  override async connectedCallback(): Promise<void> {
    super.connectedCallback();
    this._currentCompanyId = this.companyId;
    try {
      this._companies = await companyApi.listCompanies('active');
    } catch {
      // Companies list not critical - silently fail
    }
  }

  override updated(changedProps: Map<string, unknown>): void {
    if (changedProps.has('companyId')) {
      this._currentCompanyId = this.companyId;
    }
  }

  private _setTab(tab: DashboardTab): void {
    this._activeTab = tab;
  }

  private async _onCompanyChange(e: Event): Promise<void> {
    const select = e.target as HTMLSelectElement;
    const newCompanyId = select.value === '' ? null : Number(select.value);

    this._assigningCompany = true;
    try {
      await companyApi.assignProjectToCompany(this.projectId, newCompanyId);
      this._currentCompanyId = newCompanyId;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      // Show conflict hint when project is already assigned to another company
      if (msg.includes('bereits einer Firma')) {
        const confirmed = window.confirm(`${msg}\n\nZuordnung aendern?`);
        if (confirmed) {
          try {
            // Force-assign by first removing, then assigning
            await companyApi.assignProjectToCompany(this.projectId, null);
            await companyApi.assignProjectToCompany(this.projectId, newCompanyId);
            this._currentCompanyId = newCompanyId;
          } catch {
            select.value = this._currentCompanyId !== null ? String(this._currentCompanyId) : '';
          }
        } else {
          select.value = this._currentCompanyId !== null ? String(this._currentCompanyId) : '';
        }
      } else {
        select.value = this._currentCompanyId !== null ? String(this._currentCompanyId) : '';
      }
    } finally {
      this._assigningCompany = false;
    }
  }

  private _renderCompanyAssignment() {
    const currentCompany = this._companies.find(c => c.id === this._currentCompanyId);

    return html`
      <div class="cp-project-dashboard__company">
        ${currentCompany ? html`
          <span class="cp-project-dashboard__company-badge">
            ${currentCompany.icon ?? ''} ${currentCompany.name}
          </span>
        ` : ''}
        <select
          class="cp-project-dashboard__company-select"
          .value=${this._currentCompanyId !== null ? String(this._currentCompanyId) : ''}
          @change=${this._onCompanyChange}
          ?disabled=${this._assigningCompany}
          title="Firma zuordnen"
        >
          <option value="">Keine Firma</option>
          ${this._companies.map(c => html`
            <option value=${c.id} ?selected=${c.id === this._currentCompanyId}>${c.name}</option>
          `)}
        </select>
      </div>
    `;
  }

  private _renderTabContent() {
    switch (this._activeTab) {
      case 'specs':
        return html`<cp-spec-list .projectId=${this.projectId}></cp-spec-list>`;
      case 'backlog':
        return html`<cp-backlog-list .projectId=${this.projectId}></cp-backlog-list>`;
      case 'docs':
        return html`<cp-docs-viewer .projectId=${this.projectId}></cp-docs-viewer>`;
    }
  }

  render() {
    const tabs: { key: DashboardTab; label: string }[] = [
      { key: 'specs', label: 'Specs' },
      { key: 'backlog', label: 'Backlog' },
      { key: 'docs', label: 'Docs' },
    ];

    return html`
      <div class="cp-project-dashboard">
        <div class="cp-project-dashboard__header">
          <div class="cp-project-dashboard__header-top">
            <h1 class="cp-project-dashboard__title">${this.projectName}</h1>
            ${this._renderCompanyAssignment()}
          </div>
          <p class="cp-project-dashboard__path">${this.projectPath}</p>
        </div>

        <nav class="cp-project-dashboard__tabs">
          ${tabs.map(tab => html`
            <button
              class="cp-project-dashboard__tab ${this._activeTab === tab.key ? 'cp-project-dashboard__tab--active' : ''}"
              @click=${() => this._setTab(tab.key)}
            >${tab.label}</button>
          `)}
        </nav>

        <div class="cp-project-dashboard__content">
          ${this._renderTabContent()}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-project-dashboard': CpProjectDashboard;
  }
}
